import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import { upperCase } from 'lodash';
import moment from 'moment';
import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CAN from 'src/assets/img/flags/Canada-sm.svg';
import US from 'src/assets/img/flags/US-sm.svg';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import AlertMessage from 'src/components/AlertMessage';
import BadgeCmp from 'src/components/BadgeCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { QUOTE_CLASSIFICATION } from 'src/constants/common';
import { ROUTES } from 'src/constants/routes';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { setWorkflowSteps } from 'src/redux/auth.slice';
import { goToStep, selectFormData } from 'src/redux/bookingStepper.slice';
import { AppDispatch, RootState } from 'src/redux/store';
import { bookQuote } from 'src/services/QuoteService';
import { getFormattedDate, getFormattedNumber, getShortName, onError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { getRateCurrencyDisplay } from '../../../myQuote.helpers';
import { calculateBusinessDays } from '../../../quotingDashboard.helpers';
import { resolveBolRequirementsData } from '../BolRequirements/bolMentionOptions';

// 🔹 Props Types
interface InfoRowProps {
    label: string;
    value: string | number | React.ReactNode;
    valueClassName?: string;
    infoRowClassName?: string;
}

interface CardProps {
    title?: React.ReactNode;
    children: React.ReactNode;
    cardClassName?: string;
    cardBlockClassName?: string;
    headerRight?: React.ReactNode;
    onHeaderClick?: () => void;
    isBodyVisible?: boolean;
}

// 🔹 Common InfoRow Component
const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueClassName, infoRowClassName }) => {
    const valueRef = useRef<HTMLParagraphElement>(null);
    const isStringOrNumber = typeof value === 'string' || typeof value === 'number';

    return (
        <div className={`${infoRowClassName} space-y-1`}>
            <h6 className="text-gray500 text-xs leading-[18px] font-normal">{label}</h6>
            {isStringOrNumber ? (
                <TruncateTextCmp message={String(value)} refName={valueRef} tooltipType="action">
                    <div className={`${valueClassName} text-grayLight600 text-xs leading-[20px] font-medium`}>
                        <p className="truncate" ref={valueRef}>
                            {value}
                        </p>
                    </div>
                </TruncateTextCmp>
            ) : (
                <div className={`${valueClassName} text-grayLight600 text-xs leading-[20px] font-medium`}>{value}</div>
            )}
        </div>
    );
};

// 🔹 Common Card Component — header is flush with the body (plain white, no gray25 band or
// border-b divider) per figma review; this Card is local to ReviewFinalize only, so the change
// doesn't touch the shared card styling used elsewhere in the app. Border + shadow + [&>*]:px-4
// padding kept in parity with wal-frontend-v2's ReviewConfirmStep Card.
const Card: React.FC<CardProps> = ({ title, children, cardClassName, cardBlockClassName, headerRight, onHeaderClick, isBodyVisible = true }) => (
    <div className={`w-full min-w-0 ${cardClassName}`}>
        <div
            className={`rounded-lg border border-utilityGray200 bg-white shadow-[0_-2px_4px_0_rgba(0,0,0,0.04),0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)] overflow-hidden h-full [&>*]:px-4`}
        >
            {(title || headerRight) && (
                <div className={`py-3 flex items-center justify-between gap-x-2 ${onHeaderClick ? 'cursor-pointer' : ''}`} onClick={onHeaderClick}>
                    {title && <h6 className="text-grayLight900 text-sm font-medium">{title}</h6>}
                    {headerRight}
                </div>
            )}
            {isBodyVisible && <div className={`w-full pb-3 ${cardBlockClassName}`}>{children}</div>}
        </div>
    </div>
);

// Field labels inside a Route N point card. A middle stop is neither the shipper nor the consignee,
// so it reads plain "Name"/"Address" instead of borrowing the shipper's wording, and the closing
// point of the route is labelled as the consignee (client decision).
// `instruction` is absent for middle stops on purpose — a stop carries no pickup/delivery
// instruction in the booking form, so the row is simply not rendered for it.
const ROUTE_POINT_FIELD_LABELS: Record<string, { name: string; address: string; instruction?: string }> = {
    shipper: { name: 'Shipper Name', address: 'Shipper Address', instruction: 'Pickup Instructions' },
    stop: { name: 'Name', address: 'Address' },
    consignee: { name: 'Consignee Name', address: 'Consignee Address', instruction: 'Delivery Instructions' },
};

// Route N cards render label/value on one line instead of the generic InfoRow's stacked
// label-above-value layout.
//
// Layout contract (both parts matter once a value is long enough to truncate, e.g. an instruction):
//  - The label is flex-none + nowrap. It must never shrink: it has no overflow guard, so a
//    shrinkable label spills its text straight over the value instead of clipping.
//  - The value wrapper takes the remaining width (flex-1 min-w-0) and the inner p right-aligns,
//    which is what puts the value on the right edge — the label no longer does it by growing.
//
// `valueWrapClassName` caps that width for free-text values (instructions): without a cap a long
// instruction runs the full row and reads as densely as the address above it. `ml-auto` keeps the
// capped value flush right, since flex-1 can no longer grow into the leftover space.
const RoutePointInfoRow: React.FC<{ label: string; value: React.ReactNode; valueWrapClassName?: string }> = ({ label, value, valueWrapClassName = '' }) => {
    const valueRef = useRef<HTMLParagraphElement>(null);
    const isStringOrNumber = typeof value === 'string' || typeof value === 'number';

    return (
        <div className="flex items-start gap-x-2 w-full">
            <p className="flex-none text-textSecondary text-xs leading-[18px] font-normal whitespace-nowrap">{label}</p>
            {isStringOrNumber ? (
                // parentClassName styles the tooltip BUBBLE, not the trigger, and the bubble is
                // absolutely positioned — a percentage max-width there resolves against a
                // zero-width offset parent and renders the text one character per line. Keep it
                // a pixel width (same as TruncateTextCmp's own default).
                <TruncateTextCmp
                    message={String(value)}
                    refName={valueRef}
                    tooltipType="action"
                    parentClassName="max-w-[200px] lg:max-w-[400px] w-max"
                    truncateTextParentClassName={`flex-1 min-w-0 ${valueWrapClassName}`}
                >
                    <p className="truncate text-right text-textSecondary text-xs leading-[18px] font-medium" ref={valueRef}>
                        {value}
                    </p>
                </TruncateTextCmp>
            ) : (
                <div className={`flex-1 min-w-0 text-right text-textSecondary text-xs leading-[18px] font-medium ${valueWrapClassName}`}>{value}</div>
            )}
        </div>
    );
};

interface IReviewFinalizeProps {
    stepperFooterRef?: React.RefObject<any>;
}

// 🔹 Main Page Component
const ReviewFinalize = ({ stepperFooterRef }: IReviewFinalizeProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const formData = useSelector(selectFormData);
    const { workFlowSteps } = useSelector((state: RootState) => state?.auth);
    const { handlingUnitOptions, commodityOptions, appearance } = useSelector((state: RootState) => state.basic);
    const navigate = useNavigate();
    const billingAccountRef = useRef<HTMLParagraphElement>(null);
    const carrierNameRef = useRef<HTMLSpanElement>(null);
    const orgBranding = useOrgBranding();

    // Extract data from formData
    const shipperDetails = formData?.shipperDetails || {};
    const consigneeDetails = formData?.consigneeDetails || {};
    const loadDetails = formData?.loadDetails || {};
    const dimensionsDetails = loadDetails?.dimensions || [];
    const orderDetails = formData?.orderDetails || {};
    const selectedQuoteOption = formData?.selectedQuoteOption || {};
    // Carrier glyph + name must match the row the customer picked on "Select a Quote" exactly
    // (client, 2026-10-09). Mirrors SelectQuoteTableRow: same name resolution and the same
    // three-way glyph. Keep the two in step — if that row's rule changes, change this with it.
    const selectedCarrierName = selectedQuoteOption?.carrierName ?? selectedQuoteOption?.name ?? '';
    const selectedCarrierLabel = selectedCarrierName === '' ? '-' : selectedCarrierName || '-';
    // The org shown AS the carrier (carrier visibility masked) has no uploaded carrier logo, so
    // without this branch it fell through to initials ("WA") while the quote table showed the black
    // WAL mark. 'Western Alliance' is carried over from that row as a legacy name special-case.
    const isOrgAsCarrier = selectedCarrierLabel === orgBranding.name || selectedCarrierLabel === 'Western Alliance';
    const originalAddresses = formData?.originalAddresses;

    // Client decision — Route N cards: Shipper (position 1) -> middle Stops -> Consignee (last
    // position), each leg (consecutive pair) rendered as its own "Route N" section.
    const middleStops: any[] = formData?.stops || [];
    const routePoints = useMemo(() => {
        const maxPosition = 2 + middleStops.length;

        const pointAt = (position: number, type: 'shipper' | 'stop' | 'consignee', label: string, companyName?: string | null, fullAddress?: string | null, instruction?: string | null) => ({
            position,
            type,
            label,
            companyName,
            fullAddress,
            instruction,
        });

        return [
            pointAt(1, 'shipper', 'Shipper Details', shipperDetails?.shipperCompanyName, shipperDetails?.shipperFullAddress, shipperDetails?.pickupInstruction),
            ...[...middleStops].sort((a, b) => a.position - b.position).map((stop, idx) => pointAt(stop.position, 'stop', `Stop ${idx + 1}`, stop.companyName, stop.fullAddress)),
            pointAt(maxPosition, 'consignee', 'Consignee Details', consigneeDetails?.consigneeCompanyName, consigneeDetails?.consigneeFullAddress, consigneeDetails?.deliveryInstruction),
        ];
    }, [
        middleStops,
        shipperDetails?.shipperCompanyName,
        shipperDetails?.shipperFullAddress,
        shipperDetails?.pickupInstruction,
        consigneeDetails?.consigneeCompanyName,
        consigneeDetails?.consigneeFullAddress,
        consigneeDetails?.deliveryInstruction,
    ]);

    const routeLegs = useMemo(() => {
        const legs: [any, any][] = [];

        for (let i = 0; i < routePoints.length - 1; i++) {
            legs.push([routePoints[i], routePoints[i + 1]]);
        }

        return legs;
    }, [routePoints]);

    const [isLineItemsExpanded, setIsLineItemsExpanded] = useState(true);

    // Compare booking-time address against the original quoted address. Shows a
    // pricing-may-change warning when the customer swapped the shipper/consignee
    // and the city/state/country no longer match what was originally quoted.
    // Mirrors the same check rendered on the shipper/consignee step.
    const compareAddressMismatch = (current: { city?: string; state?: string; country?: string }, original?: { city?: string; state?: string; country?: string }): boolean => {
        if (!current?.city || !original?.city) return false;

        return (
            current.city?.toLowerCase() !== original.city?.toLowerCase() ||
            current.state?.toLowerCase() !== original.state?.toLowerCase() ||
            current.country?.toLowerCase() !== original.country?.toLowerCase()
        );
    };

    const hasShipperAddressMismatch = useMemo(
        () =>
            compareAddressMismatch(
                {
                    city: shipperDetails?.shipperCity,
                    state: shipperDetails?.shipperState,
                    country: shipperDetails?.shipperCountry,
                },
                originalAddresses?.shipper
            ),
        [shipperDetails?.shipperCity, shipperDetails?.shipperState, shipperDetails?.shipperCountry, originalAddresses?.shipper]
    );

    const hasConsigneeAddressMismatch = useMemo(
        () =>
            compareAddressMismatch(
                {
                    city: consigneeDetails?.consigneeCity,
                    state: consigneeDetails?.consigneeState,
                    country: consigneeDetails?.consigneeCountry,
                },
                originalAddresses?.consignee
            ),
        [consigneeDetails?.consigneeCity, consigneeDetails?.consigneeState, consigneeDetails?.consigneeCountry, originalAddresses?.consignee]
    );

    const userTimezone = appearance?.timezone || 'UTC';

    const tagType: Record<string, string> = {
        Fastest: 'lightblue',
        Cheapest: 'primary',
        Premium: 'orange',
    };

    const businessDays = useMemo(() => {
        if (!selectedQuoteOption?.projectedDeliveryDate) return null;
        const currentDate = moment.tz(userTimezone).startOf('day');
        const pickupDateValue = shipperDetails?.pickupDate ? moment.utc(shipperDetails?.pickupDate) : currentDate;
        const adjustedPickupDate = pickupDateValue.isBefore(currentDate, 'day') ? currentDate : pickupDateValue;
        const deliveryDate = moment.utc(selectedQuoteOption?.projectedDeliveryDate);

        return calculateBusinessDays(adjustedPickupDate, deliveryDate, { skipStartDate: false }) as number;
    }, [shipperDetails?.pickupDate, selectedQuoteOption?.projectedDeliveryDate, userTimezone]);

    const isDimensionsAvailable = () => {
        if (orderDetails?.orderType === QUOTE_CLASSIFICATION?.FTL) {
            return false;
        }

        if (dimensionsDetails.length === 1) {
            const dimension = dimensionsDetails[0];

            if (
                dimension?.handlingUnitNo >= 1 ||
                dimension?.length >= 1 ||
                dimension?.width >= 1 ||
                dimension?.height >= 1 ||
                dimension?.weight >= 1 ||
                dimension?.totalWeight >= 1 ||
                dimension?.freightClass >= 1
            ) {
                return true;
            }

            return false;
        }

        return dimensionsDetails.length > 0;
    };

    const handleSubmit = async () => {
        try {
            const resolvedBolData = resolveBolRequirementsData(formData?.bolRequirementsData ?? {});

            const payload = {
                quoteId: formData?.quoteId || null,
                quoteCarrierId: formData?.quoteCarrierId || null,
                shipperDetails: {
                    ...shipperDetails,
                },
                consigneeDetails: {
                    ...consigneeDetails,
                },
                dimensionsDetails: isDimensionsAvailable() ? dimensionsDetails : [],
                orderDetails: orderDetails,
                services: formData?.services || {},
                bolRequirementsData: {
                    carrierBookingShipperContactName: resolvedBolData.shipperContactName ?? '',
                    carrierBookingShipperPhone: resolvedBolData.shipperContactNumber ?? '',
                    carrierBookingShipperEmail: resolvedBolData.shipperEmail ?? '',
                    carrierBookingConsigneeContactName: resolvedBolData.consigneeContactName ?? '',
                    carrierBookingConsigneePhone: resolvedBolData.consigneeContactNumber ?? '',
                    carrierBookingConsigneeEmail: resolvedBolData.consigneeEmail ?? '',
                    bolInstructions: formData?.bolRequirementsData?.specialNotes ?? '',
                },
            };

            const isShipperCountryOnly = !!shipperDetails?.shipperCountry && shipperDetails?.shipperFullAddress?.trim() === shipperDetails?.shipperCountry?.trim();
            const shipperMissing = [!shipperDetails?.shipperCity && 'city', !shipperDetails?.shipperState && 'state', !shipperDetails?.shipperCountry && 'country'].filter(Boolean) as string[];

            if (isShipperCountryOnly || shipperMissing.length > 0) {
                dispatch(goToStep(1));
                throw new Error('Shipper address is incomplete');
            }

            const isConsigneeCountryOnly = !!consigneeDetails?.consigneeCountry && consigneeDetails?.consigneeFullAddress?.trim() === consigneeDetails?.consigneeCountry?.trim();
            const consigneeMissing = [!consigneeDetails?.consigneeCity && 'city', !consigneeDetails?.consigneeState && 'state', !consigneeDetails?.consigneeCountry && 'country'].filter(
                Boolean
            ) as string[];

            if (isConsigneeCountryOnly || consigneeMissing.length > 0) {
                dispatch(goToStep(2));
                throw new Error('Consignee address is incomplete');
            }

            const response = await bookQuote(payload);

            if (response?.data) {
                WalToast.success(`${response?.data?.quote?.id ? `#${response?.data?.quote?.code}` : ''} - Booking confirmed successfully.`);
                dispatch(setWorkflowSteps({ ...workFlowSteps, BOOK_FIRST_ORDER: true }));
                // Navigate to orders page after successful booking
                navigate(`${ROUTES.MY_QUOTE}/${formData?.quoteId}`);
            }
        } catch (error: any) {
            console.error('Error booking quote:', error);
            WalToast.error(error?.response?.data?.message || 'Booking failed');
            throw error; // Re-throw to prevent navigation
        }
    };

    // Expose triggerNext to parent via ref
    useImperativeHandle(stepperFooterRef, () => ({
        triggerNext: async () => {
            await handleSubmit();
        },
    }));

    const addressMismatchLabel =
        hasShipperAddressMismatch && hasConsigneeAddressMismatch
            ? 'The shipper and consignee addresses have been updated. Pricing may change based on the new addresses.'
            : hasShipperAddressMismatch
            ? 'The shipper address has been updated. Pricing may change based on the new address.'
            : hasConsigneeAddressMismatch
            ? 'The consignee address has been updated. Pricing may change based on the new address.'
            : '';

    return (
        <>
            <div className="w-full flex flex-wrap gap-x-3 gap-y-4">
                {(hasShipperAddressMismatch || hasConsigneeAddressMismatch) && (
                    <div className="w-full animate-field-item">
                        <AlertMessage style="info" labelInfo={addressMismatchLabel} />
                    </div>
                )}
                {/* Selected Quote Option — figma review (Lucas Bellido): Sales Representative
                    removed (unused), Division/Billing Account folded in from the old separate
                    "Booking Info" card so everything sits on one line, and the carrier glyph sized
                    down to match the smaller (xs) avatar convention used elsewhere in this bar. */}
                <Card title="Selected Quote Option" cardClassName="animate-field-item" cardBlockClassName="flex flex-nowrap gap-x-3">
                    <InfoRow label="Division" value={orderDetails?.divisionName || '-'} infoRowClassName="flex-1 min-w-[120px] max-w-[180px]" />
                    <InfoRow
                        label="Billing Account"
                        valueClassName="flex items-center gap-x-1.5 cursor-pointer"
                        value={
                            <>
                                {orderDetails?.billingAccount?.currency && (
                                    <img
                                        src={orderDetails?.billingAccount?.currency?.toUpperCase() === 'CAD' ? CAN : US}
                                        alt={orderDetails?.billingAccount?.currency?.toUpperCase() === 'CAD' ? 'Canada' : 'United States'}
                                        className="w-4 h-4 object-cover rounded flex-none"
                                    />
                                )}
                                <TruncateTextCmp message={orderDetails?.billingAccount?.displayName || '-'} refName={billingAccountRef} tooltipType="action">
                                    <p className="truncate" ref={billingAccountRef}>
                                        {orderDetails?.billingAccount?.displayName || '-'}
                                    </p>
                                </TruncateTextCmp>
                            </>
                        }
                        infoRowClassName="min-w-[140px] max-w-[180px]"
                    />
                    <InfoRow
                        label="Carrier"
                        valueClassName="flex items-center gap-x-2"
                        infoRowClassName="min-w-[140px] max-w-[180px]"
                        value={
                            <>
                                <div className="flex-none">
                                    {selectedQuoteOption?.image && selectedQuoteOption?.imageUrl ? (
                                        <img
                                            src={`${selectedQuoteOption.imageUrl}${selectedQuoteOption.image}`}
                                            alt={selectedCarrierName}
                                            className="table-profile-icn-circle-xs rounded-md object-contain"
                                            onError={onError}
                                        />
                                    ) : isOrgAsCarrier ? (
                                        <div className="table-profile-customer-circle-xs rounded-md !bg-utilityBlack flex items-center justify-center p-0.5">
                                            <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                                        </div>
                                    ) : (
                                        <div className="table-profile-customer-circle-xs rounded-md">{getShortName(selectedCarrierName)}</div>
                                    )}
                                </div>
                                <TruncateTextCmp message={selectedCarrierLabel} refName={carrierNameRef}>
                                    <span ref={carrierNameRef} className="text-utilityBlack800 font-semibold truncate block">
                                        {selectedCarrierLabel}
                                    </span>
                                </TruncateTextCmp>
                            </>
                        }
                    />
                    <InfoRow
                        label="Tag"
                        infoRowClassName="flex-none min-w-[100px] max-w-[180px]"
                        value={
                            selectedQuoteOption?.tag && tagType[selectedQuoteOption.tag] ? (
                                <BadgeCmp style="modern" type={tagType[selectedQuoteOption.tag] as any}>
                                    {selectedQuoteOption.tag}
                                </BadgeCmp>
                            ) : (
                                '-'
                            )
                        }
                    />
                    <InfoRow label="Mode" value={selectedQuoteOption?.shippingMode || '-'} infoRowClassName="flex-none min-w-[90px] max-w-[180px]" />
                    <InfoRow label="Service Level" value={selectedQuoteOption?.shippingServiceLevel || '-'} infoRowClassName="flex-1 min-w-[120px] max-w-[180px]" />
                    <InfoRow
                        label="Transit Time"
                        infoRowClassName="flex-1 min-w-[120px] max-w-[180px]"
                        value={selectedQuoteOption?.projectedDeliveryDate && businessDays && businessDays > 0 ? `Est. ${businessDays} ${businessDays === 1 ? 'day' : 'days'}` : '-'}
                    />
                    <InfoRow
                        label="Total Sale Price"
                        // Currency follows the quote's billing account (getRateCurrencyDisplay) — same rule
                        // as the rate table and invoicing. Previously hardcoded to CAD.
                        value={(() => {
                            const { amount, label } = getRateCurrencyDisplay(selectedQuoteOption);

                            return amount ? `$${getFormattedNumber(amount)} ${label}` : '-';
                        })()}
                        infoRowClassName="flex-1 min-w-[120px] max-w-[180px]"
                    />
                </Card>

                {/* Route N — figma node 6022:139170: ONE shared gray50/bordered panel wraps every
                    leg (not a separate box per leg) — each leg inside it is just a heading + a row
                    of two bordered/shadowed white point cards (parity with wal-frontend-v2's Card
                    shadow+border). The two field labels inside a card follow the point's role —
                    see ROUTE_POINT_FIELD_LABELS. */}
                {routeLegs.length > 0 && (
                    <div className="w-full rounded-xl border border-utilityGray200 bg-gray50 p-4 flex flex-col gap-4 animate-field-item">
                        {routeLegs.map(([from, to], legIndex) => (
                            <div key={`route-${from.position}-${to.position}`} className="w-full flex flex-col gap-2">
                                <p className="text-textSecondary text-sm leading-5 font-medium w-full">Route {legIndex + 1}</p>
                                {/* items-stretch, not items-start: the two cards in a leg hold a different
                                    number of rows whenever one side carries an instruction, so the shorter
                                    card must stretch to the taller one instead of ending early. */}
                                <div className="flex flex-wrap gap-x-4 gap-y-3 items-stretch w-full">
                                    {[from, to].map((point) => (
                                        <div
                                            key={point.position}
                                            className="flex-1 min-w-0 bg-white rounded-lg border border-utilityGray200 shadow-[0_-2px_4px_0_rgba(0,0,0,0.04),0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)]"
                                        >
                                            <div className="flex flex-col gap-3 px-4 py-3 w-full">
                                                <div className="flex items-center justify-between gap-x-2 w-full">
                                                    <p className="flex-1 min-w-0 truncate text-grayLight900 text-sm leading-5 font-medium">{point.label}</p>
                                                </div>
                                                <div className="flex flex-col gap-y-1.5 w-full">
                                                    <RoutePointInfoRow label={ROUTE_POINT_FIELD_LABELS[point.type].name} value={point.companyName || '-'} />
                                                    <RoutePointInfoRow label={ROUTE_POINT_FIELD_LABELS[point.type].address} value={point.fullAddress || '-'} />
                                                    {/* Instructions are free text (up to 200 chars from the Shipper/Consignee step),
                                                        so the row relies on RoutePointInfoRow's TruncateTextCmp: one truncated line,
                                                        full text in a hover tooltip only when it actually overflows.
                                                        The whole row is dropped when nothing was entered — unlike Name/Address this
                                                        field is optional, so a bare dash would read as a missing value rather than
                                                        an unused one. Middle stops have no instruction label at all. */}
                                                    {ROUTE_POINT_FIELD_LABELS[point.type].instruction && String(point.instruction ?? '').trim() ? (
                                                        <RoutePointInfoRow
                                                            label={ROUTE_POINT_FIELD_LABELS[point.type].instruction!}
                                                            value={String(point.instruction).trim()}
                                                            valueWrapClassName="max-w-[55%] ml-auto"
                                                        />
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Order Details */}
                <Card title="Order Details" cardClassName="animate-field-item" cardBlockClassName="flex flex-wrap gap-x-3 gap-y-4">
                    <InfoRow
                        label="Order Type"
                        // No order type means no badge — a green badge with a dash in it reads as a
                        // value. Falls back to the plain dash the rest of the card uses.
                        value={
                            orderDetails?.orderType ? (
                                <BadgeCmp style="modern" type="success">
                                    {upperCase(orderDetails.orderType)}
                                </BadgeCmp>
                            ) : (
                                '-'
                            )
                        }
                        infoRowClassName="w-[calc(20%_-_10px)]"
                    />
                    <InfoRow label="Equipment Type" value={orderDetails?.equipmentTypeFullName || '-'} infoRowClassName="w-[calc(20%_-_10px)]" />
                    <InfoRow
                        label="Total Weight"
                        value={loadDetails?.totalWeight ? `${loadDetails?.totalWeight} ${loadDetails?.weightMeasurement?.toLowerCase() || 'lbs'}` : '-'}
                        infoRowClassName="w-[calc(20%_-_10px)]"
                    />
                    <InfoRow label="Linear Footage" value={loadDetails?.linearFootage ? `${loadDetails?.linearFootage} ft` : '-'} infoRowClassName="w-[calc(20%_-_10px)]" />
                    {/* getFormattedDate returns an empty string for a missing date, so both of these
                        rendered a blank cell instead of the dash every other empty field shows. */}
                    <InfoRow label="Pickup Date" value={getFormattedDate(shipperDetails?.pickupDate, 'ddd MMM Do, YYYY') || '-'} infoRowClassName="w-[calc(20%_-_10px)]" />
                    <InfoRow label="Estimated Delivery Date" value={getFormattedDate(consigneeDetails?.deliveryDate, 'ddd MMM Do, YYYY') || '-'} infoRowClassName="w-[calc(20%_-_10px)]" />
                    {/* Optional reference identifiers — these are blank on most bookings, and a label
                        sitting above a dash reads as missing data rather than "not applicable". Each
                        one only renders once the Order Details step actually captured a value. */}
                    {orderDetails?.refNumber ? <InfoRow label="Reference Number" value={orderDetails.refNumber} infoRowClassName="w-[calc(20%_-_10px)]" /> : null}
                    {orderDetails?.poNumber ? <InfoRow label="PO Number" value={orderDetails.poNumber} infoRowClassName="w-[calc(20%_-_10px)]" /> : null}
                    {orderDetails?.pickupNumber ? <InfoRow label="Pickup Number" value={orderDetails.pickupNumber} infoRowClassName="w-[calc(20%_-_10px)]" /> : null}
                    {orderDetails?.deliveryNumber ? <InfoRow label="Delivery Number" value={orderDetails.deliveryNumber} infoRowClassName="w-[calc(20%_-_10px)]" /> : null}
                    {orderDetails?.isLoadInsuranceEnabled && (
                        <>
                            <InfoRow
                                label="Insurance Commodity"
                                value={orderDetails?.insuranceCommodity ? String(orderDetails.insuranceCommodity).split('|').filter(Boolean).join(', ') : '-'}
                                infoRowClassName="w-[calc(20%_-_10px)]"
                            />
                            <InfoRow
                                label="Commercial Value"
                                value={
                                    orderDetails?.insuranceCommercialValue ? `$${getFormattedNumber(orderDetails.insuranceCommercialValue, true)} ${orderDetails?.insuranceCurrencyType || ''}` : '-'
                                }
                                infoRowClassName="w-[calc(20%_-_10px)]"
                            />
                        </>
                    )}
                </Card>

                {/* Line Items — collapsible, with a count badge and rotating chevron matching the
                    approved reference design. */}
                {isDimensionsAvailable() && dimensionsDetails.length > 0 && (
                    <Card
                        title={
                            <span className="flex items-center gap-x-1.5">
                                Line Items
                                <BadgeCmp style="modern" type="gray" isHidePillDot mainClassName="bg-white !py-px !px-[5px]">
                                    {dimensionsDetails.length}
                                </BadgeCmp>
                            </span>
                        }
                        cardClassName="animate-field-item"
                        cardBlockClassName="divide-y divide-utilityGray200 [&>*]:py-3 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0"
                        onHeaderClick={() => setIsLineItemsExpanded((prev) => !prev)}
                        isBodyVisible={isLineItemsExpanded}
                        headerRight={<ChevronDown className={`w-4 h-4 text-gray500 transition-transform flex-none ${isLineItemsExpanded ? 'rotate-180' : ''}`} />}
                    >
                        {dimensionsDetails?.map((dimension: any, index: number) => (
                            <React.Fragment key={dimension?.id || index}>
                                <div className="flex flex-wrap gap-x-3 gap-y-4">
                                    <InfoRow
                                        label="Total Quantity"
                                        // Used to print "0" (plus a trailing space when the unit was unknown) for a line
                                        // with no quantity. A missing quantity is missing data, so it reads as a dash.
                                        value={
                                            dimension?.handlingUnitNo
                                                ? `${dimension.handlingUnitNo} ${
                                                      handlingUnitOptions?.find((item: any) => item.value === dimension?.handlingUnit)?.label ?? dimension?.handlingUnit ?? ''
                                                  }`.trim()
                                                : '-'
                                        }
                                        infoRowClassName="w-[calc(20%_-_10px)]"
                                    />
                                    <InfoRow
                                        label="Dimensions"
                                        value={dimension?.length && dimension?.width && dimension?.height ? `${dimension?.length}" × ${dimension?.width}" × ${dimension?.height}"` : '-'}
                                        infoRowClassName="w-[calc(20%_-_10px)]"
                                    />
                                    <InfoRow label="Freight Class" value={dimension?.freightClass || '-'} infoRowClassName="w-[calc(20%_-_10px)]" />
                                    <InfoRow
                                        label="Total Weight"
                                        value={
                                            dimension?.totalWeight
                                                ? `${getFormattedNumber(dimension?.totalWeight, true, true)} ${
                                                      dimension?.weightMeasurement?.toLowerCase() || loadDetails?.weightMeasurement?.toLowerCase() || 'lbs'
                                                  }`
                                                : '-'
                                        }
                                        infoRowClassName="w-[calc(20%_-_10px)]"
                                    />
                                    {/* Bug fix — this cell used to build its own TruncateTextCmp around a ref declared
                                        once for the whole component, so every line item's commodity measured the same
                                        element (the last one rendered) and none of them got a tooltip. Handing InfoRow a
                                        plain string instead lets it run its own per-row truncation, which is what every
                                        other cell here already does. */}
                                    <InfoRow
                                        label="Commodity"
                                        value={
                                            (commodityOptions?.find((e: any) => e?.value?.trim()?.toLowerCase() === dimension?.commodity?.trim()?.toLowerCase()) as any)?.label ||
                                            dimension?.commodity ||
                                            '-'
                                        }
                                        infoRowClassName="w-[calc(20%_-_10px)]"
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                    </Card>
                )}
            </div>
        </>
    );
};

export default ReviewFinalize;
